import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './pages/Login';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import ContentEditor from './pages/ContentEditor';
import Products from './pages/Products';
import Teams from './pages/Teams';
import Players from './pages/Players';
import Registrations from './pages/Registrations';
import Coaches from './pages/Coaches';
import SupportStaff from './pages/SupportStaff';
import Members from './pages/Members';
import Guardians from './pages/Guardians';
import Memberships from './pages/Memberships';
import Subscriptions from './pages/Subscriptions';
import Payers from './pages/Payers';
import Orders from './pages/Orders';
import Settings from './pages/Settings';
import CustomDomain from './pages/CustomDomain';
import News from './pages/News';
import Sponsors from './pages/Sponsors';
import Affiliations from './pages/Affiliations';
import Venues from './pages/Venues';
import Events from './pages/Events';
import Gallery from './pages/Gallery';
import Documents from './pages/Documents';
import Fixtures from './pages/Fixtures';
import Upgrade from './pages/Upgrade';
import EmailCompose from './pages/EmailCompose';
import EmailHistory from './pages/EmailHistory';
import EmailSubscribers from './pages/EmailSubscribers';
import EmailSuppressions from './pages/EmailSuppressions';
import EmailGroups from './pages/EmailGroups';
import Sidebar from './components/Sidebar';
import WebsiteNav from './components/WebsiteNav';
import ImpersonationBanner from './components/ImpersonationBanner';
import PreviewBar from './components/PreviewBar';
import './App.css';

function AppContent() {
  const { isAuthenticated, isLoading, logout, features, plan } = useAuth();
  const location = useLocation();

  // Allow the password-reset page to render without auth. The reset
  // link emailed to a locked-out admin lands here with ?token=&email=
  // query params; ResetPassword consumes them and posts to the public
  // /api/v1/auth/reset-password endpoint. Returning early here keeps
  // the rest of the auth gate untouched.
  if (location.pathname === '/reset-password') {
    return <ResetPassword />;
  }

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Login />;
  }

  // Players is still a staged-rollout feature (per-club flag).
  // Email used to be gated by features.email_list but is now purely
  // plan-based (Club + Pro), see lambda/routes/email.ts comment.
  const playersEnabled = features.players === true;
  // Members spine (Members / Coaches / Guardians tabs), staged per-club
  // flag, flipped via SuperAdmin. The lambda routes 404 when off, so the
  // routes stay hidden until the club is opted in.
  const membersEnabled = features.members === true;
  // Collections (membership fees by card), staged per-club flag.
  const collectionsEnabled = features.collections === true;
  const fixturesEnabled = features.fixtures === true;
  const registrationEnabled = features.registration === true;
  // Custom Domain (BYOD), operator-set flag only. Pro gets BYOD
  // included; Club/Free can subscribe as a paid add-on (£3/mo or
  // £5/mo + £15 setup), so every plan with the flag on needs the
  // route to either show the upsell CTA or the management surface.
  // Lambda enforces billing, 402 with an actionable message, and
  // the card reads /byod/billing-status separately to decide UX.
  const emailListEnabled = plan === 'club' || plan === 'pro';
  // Club Staff, a club-wide people page, Club + Pro only (direct plan gate).
  const clubStaffEnabled = plan === 'club' || plan === 'pro';

  return (
    <div className="app-shell">
      <ImpersonationBanner />
      <PreviewBar />
      <div className="app-container">
      <Sidebar onLogout={logout} />
      <main className="main-content">
        <WebsiteNav />
        <Routes>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/content" element={<ContentEditor />} />
          <Route path="/products" element={<Products />} />
          <Route path="/teams" element={<Teams />} />
          {(playersEnabled || collectionsEnabled) && <Route path="/players" element={<Players />} />}
          {(membersEnabled || collectionsEnabled) && <Route path="/members" element={<Members />} />}
          {membersEnabled && <Route path="/coaches" element={<Coaches />} />}
          {(membersEnabled || collectionsEnabled) && <Route path="/guardians" element={<Guardians />} />}
          {collectionsEnabled && <Route path="/collections" element={<Memberships />} />}
          {collectionsEnabled && <Route path="/collections/subscriptions" element={<Subscriptions />} />}
          {collectionsEnabled && <Route path="/collections/payers" element={<Payers />} />}
          {clubStaffEnabled && <Route path="/club-staff" element={<SupportStaff />} />}
          <Route path="/gallery" element={<Gallery />} />
          <Route path="/sponsors" element={<Sponsors />} />
          <Route path="/affiliations" element={<Affiliations />} />
          <Route path="/venues" element={<Venues />} />
          <Route path="/events" element={<Events />} />
          <Route path="/documents" element={<Documents />} />
          {fixturesEnabled && <Route path="/fixtures" element={<Fixtures />} />}
          {registrationEnabled && <Route path="/registrations" element={<Registrations />} />}
          <Route path="/news" element={<News />} />
          <Route path="/orders" element={<Orders />} />
          {emailListEnabled && <Route path="/email/compose" element={<EmailCompose />} />}
          {emailListEnabled && <Route path="/email/history" element={<EmailHistory />} />}
          {emailListEnabled && <Route path="/email/subscribers" element={<EmailSubscribers />} />}
          {emailListEnabled && <Route path="/email/suppressions" element={<EmailSuppressions />} />}
          {emailListEnabled && <Route path="/email/groups" element={<EmailGroups />} />}
          <Route path="/settings" element={<Settings />} />
          {/* Open to every club: the domain is an add-on any plan can buy and the
              page's own pricing card is what sells it. Paying is the gate,
              enforced in lambda/routes/domains.ts. */}
          <Route path="/custom-domain" element={<CustomDomain />} />
          <Route path="/upgrade" element={<Upgrade />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </main>
      </div>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
